import { useEffect, useRef, useState, useCallback } from 'react';
import type { ClaudeEvent, ConnectionStatus } from '../types';

interface UseWebSocketOptions {
  onEvent?: (event: ClaudeEvent) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { 
    onEvent, 
    reconnectInterval = 2000,
    maxReconnectAttempts = 10,
  } = options;
  
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [lastMessage, setLastMessage] = useState<ClaudeEvent | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout>>();

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Use configured API URL or fall back to same-origin
    const apiBase = import.meta.env.VITE_API_URL || '';
    let wsUrl: string;

    if (apiBase) {
      // Convert https:// to wss:// or http:// to ws://
      wsUrl = apiBase.replace(/^http/, 'ws') + '/api/ws';
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/api/ws`;
    }
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        reconnectAttempts.current = 0;
      };

      ws.onclose = () => {
        setStatus('disconnected');
        wsRef.current = null;

        // Attempt reconnect
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          reconnectTimeout.current = setTimeout(connect, reconnectInterval);
        }
      };

      ws.onerror = () => {
        ws.close();
      };

      ws.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data) as ClaudeEvent;
          setLastMessage(event);
          onEvent?.(event);
        } catch {
          // Handle pong or invalid messages
          if (e.data !== 'pong') {
            console.warn('Invalid WebSocket message:', e.data);
          }
        }
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      setStatus('disconnected');
    }
  }, [onEvent, reconnectInterval, maxReconnectAttempts]);

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send('ping');
    }
  }, []);

  useEffect(() => {
    connect();

    // Ping every 30 seconds to keep connection alive
    const pingInterval = setInterval(sendPing, 30000);

    return () => {
      clearInterval(pingInterval);
      disconnect();
    };
  }, [connect, disconnect, sendPing]);

  return {
    status,
    lastMessage,
    reconnect: connect,
    disconnect,
  };
}
